'use strict';
import { module } from '../app';
import moment from 'moment';
import * as commonConstants from '../constants/constantStrings';

/**
 * Service to handle follow-up operations for patients.
 * Extracted from patient.js for better code organization and maintainability.
 */
module.factory('followUpOperations', [
  'FollowUpsDomain',
  'FollowUpDetailDialogModel',
  'FollowUpResolveDialogModel',
  'modalService',
  '$ionicLoading',
  '$rootScope',
  function (
    FollowUpsDomain,
    FollowUpDetailDialogModel,
    FollowUpResolveDialogModel,
    modalService,
    $ionicLoading,
    $rootScope
  ) {
    const buildReferringPhysician = (patient) => {
      if (!patient || !patient.RefPhyId) {
        return null;
      }

      const { RefPhyId, RefPhyFirstName, RefPhyLastName } = patient;
      const firstName = RefPhyFirstName && RefPhyFirstName.trim() || '';
      const lastName = RefPhyLastName && RefPhyLastName.trim() || '';
      const refNum = RefPhyId && RefPhyId.trim() || '';

      return {
        RefNum: refNum,
        FirstName: RefPhyFirstName,
        LastName: RefPhyLastName,
        DisplayName: `${lastName} ${firstName} (${refNum})`.trim()
      };
    };

    const buildFollowUpDetails = (patient, findingsString, followUp) => {
      const studyDate = moment(patient.Study.StudyDate).format('MM/DD/YYYY');
      return {
        Exam: `${studyDate} - ${patient.ProcedureDescription}`,
        Findings: findingsString,
        Accession: patient.Accession,
        followUpNumber: followUp && followUp.Number || 0,
      };
    };

    const formatRecallDate = (date) => moment(date).format('MM/DD/YYYY');

    const handleFollowUpError = (error) => {
      let message = error && error.statusText || 'An error occurred';
      if (error && error.status === -1) {
        message = 'Network unavailable. Please check your network settings.';
      }
      modalService.showAlert(message);
    };

    const validateOperationStatus = async (checkIfNotOffline) => {
      $ionicLoading.show({ template: commonConstants.processingLoaderMessage });
      const isOnline = await checkIfNotOffline();
      $ionicLoading.hide();
      
      return isOnline;
    };

    return {
      editFollowUp: async function (patient, followUp, isViewOnly, checkIfNotOffline, onSuccess) {
        try {
          const refPhy = buildReferringPhysician(patient);
          
          let findingsString = '';
          if (followUp) {
            findingsString = await FollowUpsDomain.getFindingsString(
              patient.Ssn,
              followUp.SDate,
              followUp.Number
            );
          }

          const customFollowUpDetails = buildFollowUpDetails(patient, findingsString, followUp);
          
          const followUpModel = new FollowUpDetailDialogModel(
            patient.Ssn,
            patient.SDate,
            customFollowUpDetails,
            refPhy,
            followUp && followUp.Id || null,
            !isViewOnly,
            checkIfNotOffline
          );

          const closeByButtonsClickOnly = true;
          const result = await modalService.showDialogWindow(
            'views/templates/followUpEditDialog.html',
            followUpModel,
            closeByButtonsClickOnly
          );

          if (onSuccess) {
            await onSuccess();
          }

          return result;
        } catch (error) {
          console.error('Error in editFollowUp:', error);
          throw error;
        }
      },

      resolveFollowUp: async function (
        patient,
        followUp,
        currentTab,
        followUpsTab,
        checkIfStillOwningAndNotOffline,
        onSuccess
      ) {
        if (currentTab !== followUpsTab) {
          return;
        }

        try {
          const lettersHaveBeenSent = followUp.LettersSent > 0;
          const recallDateFormatted = formatRecallDate(followUp.RecallDate);

          const hideConfirmation = $rootScope.techpadSettings &&
            $rootScope.techpadSettings.IsHideConfirmationWhenResolvingFollowUp === true;

          if (hideConfirmation) {
            if (checkIfStillOwningAndNotOffline) {
              $ionicLoading.show({ template: commonConstants.processingLoaderMessage });
              const canProceed = await checkIfStillOwningAndNotOffline();
              $ionicLoading.hide();
              if (!canProceed) {
                return;
              }
            }

            followUp.FUReason = followUp.FUReason || 'I';

            $ionicLoading.show({ template: commonConstants.DEFAULT_RESOLVE_FU_LOADING_MESSAGE });
            await FollowUpsDomain.resolveFollowUp(followUp);
            $ionicLoading.hide();

            if (onSuccess) {
              await onSuccess();
            }
            return;
          }

          const showResolveModal = async () => {
            const resolveDialogModel = new FollowUpResolveDialogModel(
              patient.PatientAccessToken,
              patient.Study.Clip,
              followUp,
              checkIfStillOwningAndNotOffline
            );

            const closeByButtonsClickOnly = true;
            const response = await modalService.showDialogWindow(
              'views/templates/followUpResolveDialog.html',
              resolveDialogModel,
              closeByButtonsClickOnly
            );

            if (!response.isCanceled && onSuccess) {
              await onSuccess();
            }

            return response;
          };

          // If no letters have been sent, show confirmation first
          if (!lettersHaveBeenSent) {
            const confirmationMessage = `This follow-up record is not due until ${recallDateFormatted}`;
            await modalService.showConfirmation(showResolveModal, confirmationMessage);
          } else {
            await showResolveModal();
          }
        } catch (error) {
          console.error('Error in resolveFollowUp:', error);
          throw error;
        }
      },

      reopenFollowUp: async function (patientSsn, followUp, checkIfNotOffline, onSuccess) {
        const recallDateFormatted = formatRecallDate(followUp.RecallDate);
        const confirmationMessage = `Are you sure you want to reopen this follow-up due on date ${recallDateFormatted}?`;

        const executeReopen = async () => {
          const canProceed = await validateOperationStatus(checkIfNotOffline);
          if (!canProceed) {
            return;
          }

          try {
            await FollowUpsDomain.reopenFollowUp(patientSsn, followUp.Id);
            if (onSuccess) {
              await onSuccess();
            }
          } catch (error) {
            handleFollowUpError(error);
          }
        };

        await modalService.showConfirmation(executeReopen, confirmationMessage);
      },

      deleteFollowUp: async function (followUp, checkIfNotOffline, onSuccess) {
        const recallDateFormatted = formatRecallDate(followUp.RecallDate);
        const confirmationMessage = `Are you sure you want to delete this follow-up due on date ${recallDateFormatted}?`;

        const executeDelete = async () => {
          const canProceed = await validateOperationStatus(checkIfNotOffline);
          if (!canProceed) {
            return;
          }

          try {
            await FollowUpsDomain.deleteFollowUp(followUp.Id);
            if (onSuccess) {
              await onSuccess();
            }
          } catch (error) {
            handleFollowUpError(error);
          }
        };

        await modalService.showConfirmation(executeDelete, confirmationMessage);
      }
    };
  }
]);




// WEBPACK FOOTER //
// ./src/scripts/services/followUpOperations.js